import type { StackOption } from "@sticker-giant/sg-frontend-common";

export interface ValidStackOption {
  itemsPerStack: number;
  numStacks: number;
}

export interface StackSelection {
  rollCount?: number;
  rollQty?: number;
}

interface ResolveStackSelectionParams {
  selectedQuantity: number | undefined;
  selectedRollCount: number | null;
  selectedRollQty: number | null;
  stackOptions: ValidStackOption[];
  defaultStackOption: ValidStackOption | undefined;
}

export function toValidStackOption(option: StackOption | undefined): ValidStackOption | undefined {
  if (option?.itemsPerStack === undefined || option.numStacks === undefined) return undefined;

  return { itemsPerStack: option.itemsPerStack, numStacks: option.numStacks };
}

/**
 * Resolves the `rollQty`/`rollCount` pair required by cart service from the currently selected
 * quantity and stack selection, plus the stackLayout data from the pricing service.
 */
export function resolveStackSelection({
  selectedQuantity,
  selectedRollCount,
  selectedRollQty,
  stackOptions,
  defaultStackOption,
}: ResolveStackSelectionParams): StackSelection | undefined {
  if (selectedQuantity === undefined || stackOptions.length === 0) return undefined;

  const selectedOption = stackOptions.find(option => {
    if (selectedRollCount === null && selectedRollQty === null) return false;
    if (selectedRollCount !== null && selectedRollQty !== null) {
      return option.numStacks === selectedRollCount && option.itemsPerStack === selectedRollQty;
    }
    if (selectedRollCount !== null) return option.numStacks === selectedRollCount;
    return option.itemsPerStack === selectedRollQty;
  });

  const resolvedOption = selectedOption ?? defaultStackOption ?? stackOptions[0];

  return { rollCount: resolvedOption.numStacks, rollQty: resolvedOption.itemsPerStack };
}

function getStackRange(minStacks: number | undefined, maxStacks: number | undefined) {
  if (minStacks === undefined || maxStacks === undefined) return undefined;
  if (minStacks <= 0 || maxStacks < minStacks) return undefined;

  return { minStacks, maxStacks };
}

function getGeneratedStackOptions(selectedQuantity: number, minStacks: number, maxStacks: number) {
  const options: ValidStackOption[] = [];

  for (let stackCount = minStacks; stackCount <= maxStacks; stackCount += minStacks) {
    const itemsPerStack = Math.ceil(selectedQuantity / stackCount);
    if (itemsPerStack <= 0) continue;

    options.push({
      numStacks: stackCount,
      itemsPerStack,
    });
  }

  return options;
}

export function buildStackOptions({
  selectedQuantity,
  minStacks,
  maxStacks,
  defaultStackOption,
}: {
  selectedQuantity: number | undefined;
  minStacks: number | undefined;
  maxStacks: number | undefined;
  defaultStackOption: ValidStackOption | undefined;
}): ValidStackOption[] {
  const fallbackOptions = defaultStackOption ? [defaultStackOption] : [];
  if (selectedQuantity === undefined) return fallbackOptions;

  const stackRange = getStackRange(minStacks, maxStacks);
  if (!stackRange) return fallbackOptions;

  const options = getGeneratedStackOptions(selectedQuantity, stackRange.minStacks, stackRange.maxStacks);
  if (options.length === 0) return fallbackOptions;

  if (!defaultStackOption) return options;

  const hasDefaultOption = options.some(
    option =>
      option.numStacks === defaultStackOption.numStacks && option.itemsPerStack === defaultStackOption.itemsPerStack,
  );
  if (hasDefaultOption) return options;

  return [defaultStackOption, ...options];
}
